/**
 * S3 Stages tab: which capability-spine stages this version implements, plus the
 * Industry Pack seed reference. Archetype has moved to the Governance tab so it
 * sits alongside compute classes (which filter the available archetype options).
 */

import React, { useMemo } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { STUDIO_SPINE_STAGES } from '@/types/aiStudioAgent';
import type {
  AiStudioIndustryPack,
  StudioSpineStage,
  UpdateVersionRequest,
} from '@/types/aiStudioAgent';

interface VersionStagesTabProps {
  enabledStages: StudioSpineStage[];
  industryPackId: string | null;
  packs: AiStudioIndustryPack[];
  disabled: boolean;
  onChange: (patch: UpdateVersionRequest) => void;
}

const FIELD_CLASS =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-60';
const LABEL_CLASS = 'mb-1 block text-base font-medium text-[var(--color-surface-text)]';
const HINT_CLASS = 'mb-2 text-base text-[var(--color-surface-text-secondary)]';

export const VersionStagesTab: React.FC<VersionStagesTabProps> = ({
  enabledStages,
  industryPackId,
  packs,
  disabled,
  onChange,
}) => {
  const { t } = useLanguage();

  // WF-F2: only published packs are seeds for versions/bindings. Keep the currently
  // selected pack visible even if it was linked while still a draft.
  const selectablePacks = useMemo(
    () =>
      packs.filter(
        (pack) => pack.lifecycleStatus === 'published' || pack.id === industryPackId
      ),
    [industryPackId, packs]
  );

  const toggleStage = (stage: StudioSpineStage, enabled: boolean) => {
    // Keep spine order stable regardless of the click order.
    const next = STUDIO_SPINE_STAGES.filter((candidate) =>
      candidate === stage ? enabled : enabledStages.includes(candidate)
    );
    onChange({ enabledStages: next });
  };

  return (
    <div className="flex flex-col gap-4">
      <fieldset>
        <legend className={LABEL_CLASS}>{t('aiStudio.versions.stages')}</legend>
        <p className={HINT_CLASS}>{t('aiStudio.versions.stagesHint')}</p>
        <div className="flex flex-wrap gap-4">
          {STUDIO_SPINE_STAGES.map((stage) => (
            <label
              key={stage}
              className="flex items-center gap-2 text-base text-[var(--color-surface-text)]"
            >
              <input
                type="checkbox"
                checked={enabledStages.includes(stage)}
                disabled={disabled}
                onChange={(event) => toggleStage(stage, event.target.checked)}
              />
              {t(`aiStudio.versions.stage.${stage}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={LABEL_CLASS} htmlFor="version-pack">
            {t('aiStudio.versions.industryPack')}
          </label>
          <select
            id="version-pack"
            className={FIELD_CLASS}
            value={industryPackId ?? ''}
            disabled={disabled}
            onChange={(event) => onChange({ industryPackId: event.target.value || null })}
          >
            <option value="">{t('aiStudio.common.none')}</option>
            {selectablePacks.map((pack) => (
              <option key={pack.id} value={pack.id}>
                {pack.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.versions.industryPackHint')}
          </p>
        </div>
      </div>
    </div>
  );
};
